// Custom components
import useApi from '@/lib/api/useApi';

// Types
import { IApiResponse } from '@/@types/lib/api';
import { UpitnikOdgovorStatus } from '@/models/groblja/upitnik_odgovori';

export type UpitnikMode = 'form' | 'zatvoren' | 'nevazeci' | 'preusmjeren';

export type UpitnikPreview = {
  url: string;
  layer: string;
  format: string | null;
  bbox: number[]; // EPSG:3765
  viewparams?: string | null;
};

export type Upitnik = {
  mode: UpitnikMode;
  groblje?: string;
  odjel?: string | null;
  polje?: string | null;
  red?: string | null;
  broj?: string | null;
  preview?: UpitnikPreview | null;
  slika?: boolean; // main photo of the grave exists, fetched separately
  url?: string; // preusmjeren
};

export type UpitnikOdgovor = {
  ime: string;
  prezime: string;
  privola: boolean;
  oib?: string | null;
  kontakt?: string | null;
  ulica?: string | null;
  mjesto?: string | null;
  ptt?: number | null;
  drzava?: string | null;
  napomena?: string | null;
};

export type UpitnikPrivitak = {
  name: string;
  type: string;
  size: number;
  content: string;
};

// NEMA (no code yet) and ISTEKAO (AKTIVAN past vrijedi_do) are derived, not stored
export type UpitnikStatus = 'NEMA' | 'AKTIVAN' | 'ZATVOREN' | 'PREUSMJEREN' | 'ISTEKAO';

// one printed slip, from the grave list (reprint) or from creating codes
export type UpitnikListic = {
  id: number;
  groblje_naziv: string;
  odjel?: string | null;
  polje?: string | null;
  red?: string | null;
  broj?: string | null;
  upitnik_gid: string;
  upitnik_status?: UpitnikStatus | null;
};

// grave label typed in from the slip, odjel isn't asked yet
export type UpitnikTraziParams = {
  groblje_id: number;
  odjel?: string | null;
  polje?: string | null;
  red?: string | null;
  broj?: string | null;
};

export type UpitnikTraziResult = { result: 'pronadjen'; gid: string } | { result: 'nije_pronadjen' | 'vise' };

// grave label fields the groblje asks in the lookup, in this order, never empty
export type UpitnikPolje = 'polje' | 'red' | 'broj';

export type UpitnikGroblje = { id: number; label: string; value: number; upitnik_polja: UpitnikPolje[] };

export interface IUpitnikService {
  getUpitnik: (token: string) => Promise<IApiResponse>;
  getSlika: (token: string) => Promise<IApiResponse>;
  insertOdgovor: (token: string, data: UpitnikOdgovor) => Promise<IApiResponse>;
  insertPrivitci: (token: string, gid: string, files: UpitnikPrivitak[]) => Promise<IApiResponse>;
  insertKodovi: (grobnoMjestoIds: number[]) => Promise<IApiResponse>;
  getTraziGroblja: () => Promise<IApiResponse>;
  trazi: (params: UpitnikTraziParams) => Promise<IApiResponse>;
  updateOdgovoriStatus: (ids: number[], status: UpitnikOdgovorStatus) => Promise<IApiResponse>;
  getOdgovorPrivitci: (id: number) => Promise<IApiResponse>;
}

const getUpitnik = (token: string) => useApi().get(`groblja/upitnik/${token}`);

const getSlika = (token: string) => useApi().get(`groblja/upitnik/${token}/slika`);

const insertOdgovor = (token: string, data: UpitnikOdgovor) => useApi().post(`groblja/upitnik/${token}/odgovori`, data);

const insertPrivitci = (token: string, gid: string, files: UpitnikPrivitak[]) =>
  useApi().post(`groblja/upitnik/${token}/odgovori/${gid}/privitci`, { files });

const insertKodovi = (grobnoMjestoIds: number[]) =>
  useApi().post('groblja/upitnici/kodovi', { grobno_mjesto_ids: grobnoMjestoIds });

const getTraziGroblja = () => useApi().get('groblja/upitnik-trazi/groblja');

const trazi = (params: UpitnikTraziParams) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') query.append(key, String(value));
  });
  return useApi().get(`groblja/upitnik-trazi?${query.toString()}`);
};

const updateOdgovoriStatus = (ids: number[], status: UpitnikOdgovorStatus) =>
  useApi().put('groblja/upitnici/odgovori/status', { ids, status });

const getOdgovorPrivitci = (id: number) => useApi().get(`groblja/upitnici/odgovori/${id}/privitci`);

export const upitnikService: IUpitnikService = {
  getUpitnik,
  getSlika,
  insertOdgovor,
  insertPrivitci,
  insertKodovi,
  getTraziGroblja,
  trazi,
  updateOdgovoriStatus,
  getOdgovorPrivitci,
};
